/*
* This mixin requires the following computed properties to be defined: rules, messages.
*
* Example:
*
  computed: {

    rules() {
      return {
        'loss': {
          'required': this.fields.loss !== '',
          'min': (this.fields.loss >= this.limits.loss_min_adjustment),
        },
        'expense': {
          'required': this.fields.expense !== '',
          'min': (this.fields.expense >= this.limits.expense_min_adjustment),
        },
      }
    },

    messages() {
      return {
        'loss.required': 'The loss reserve adjustment amount is required.',
        'loss.min': 'To maintain a positive final loss reserve, the minimum adjustment amount is ' + this.limits.loss_min_adjustment + '.',
        'expense.required': 'The expense reserve adjustment amount is required.',
        'expense.min': 'To maintain a positive final expense reserve, the minimum adjustment amount is ' + this.limits.expense_min_adjustment + '.',
      }
    },

  }
* */

export default {
  data() {
    return {
      validationDisplayed: false,
    }
  },

  computed: {

    /**
     * Returns the validation errors for each field based on the defined rules.
     *
     * @returns {Object} - An object containing the validation errors. The keys represent the field names
     * and the values represent the rule that failed validation for each field.
     */
    validationErrors() {
      let errorCodes = {};
      let invalidFields = [];
      for (const [field, rules] of Object.entries(this.rules)) {
        for (const [rule, validated] of Object.entries(rules)) {
          // Get only first validation error for each field.
          if (invalidFields.indexOf(field) > -1) continue;

          if (!validated) {
            invalidFields.push(field);
            errorCodes[field] = rule;
          }
        }
      }
      return errorCodes;
    },

    /**
     * Checks if there are no validation errors on any field.
     *
     * @returns {boolean} True if there are no validation errors, false otherwise.
     */
    validated() {
      return Object.entries(this.validationErrors).length === 0;
    },

    /**
     * Checks if there are any validation error on any field.
     *
     * @return {boolean} - True if there are any validation error, false otherwise.
     */
    invalidated() {
      return !this.validated;
    },

  },

  methods: {

    /**
     * Checks if the given field is valid.
     *
     * @param {string} field - The name of the field to validate.
     * @return {boolean} - True if the field is valid, false otherwise.
     */
    valid(field) {
      return this.validationErrors[field] === undefined;
    },

    /**
     * Determines if the given field is invalid.
     *
     * @param {string} field - The field to check for validity.
     * @return {boolean} - True if the field is invalid, False otherwise.
     */
    invalid(field) {
      return !this.valid(field) && this.validationDisplayed;
    },

    /**
     * Retrieves the error message for the given field if it's invalid.
     *
     * @param {string} field - The name of the field to check.
     *
     * @return {string} The error message for the specified field, or an empty string if the field is valid.
     */
    message(field) {
      if (!this.invalid(field)) return '';
      const rule = this.validationErrors[field];
      const messageCode = field + '.' + rule;
      return this.messages[messageCode] ?? '';
    },

    /**
     * Display the validation state.
     */
    displayValidation() {
      this.validationDisplayed = true;
    }

  }
}
